/**
 * New Hampshire Home Health Care Provider State-Specific Diagnostic Questions
 * 
 * REGULATORY FRAMEWORK:
 * - Primary State Regulation: He-P 809 (Home Health Care Providers)
 * - Statutory Authority: RSA 151 (Health Facilities Licensing)
 * - Patient Rights: RSA 151:21-b (Home Care Clients' Bill of Rights)
 * - Background Checks: RSA 151:2-d
 * - Abuse Reporting: RSA 161-F:46 (Adult Protective Services)
 * - Federal Standards: 42 CFR 484 (Medicare CoPs - for certified agencies)
 * 
 * REGULATION STRUCTURE (He-P 809):
 * - 809.01-03: Purpose, Scope, Definitions
 * - 809.04-06: Licensing Application and Renewal
 * - 809.07: Branch Offices
 * - 809.08: Organizational/Service Changes
 * - 809.09: Inspections
 * - 809.10: Waivers
 * - 809.11: Complaints
 * - 809.12: Administrative Remedies
 * - 809.13: Enforcement Actions
 * - 809.14: Duties and Responsibilities of All Licensees
 * - 809.15: Required Services (includes Care Plan at 809.15(o)-(q))
 * - 809.16: Medication Services
 * - 809.17: Personnel
 * - 809.18: Quality Improvement
 * - 809.19: Infection Control and Sanitation
 * - 809.20: Physical Environment and Emergency Preparedness
 * - 809.21: Community Health Services
 * 
 * KEY VERIFIED REQUIREMENTS (December 2025):
 * - License valid for 2 years
 * - Record retention: 7 years minimum
 * - Criminal background check before patient contact
 * - Administrator: Bachelor's degree in business or health-related field
 * - Director of Patient Services: 2 years supervisory experience
 * - POC submitted within 21 days after inspection findings
 * - Care plan per He-P 809.15(o)-(q)
 * 
 * Regulatory Body: New Hampshire Department of Health and Human Services (DHHS)
 * Division of Public Health Services, Health Facilities Administration
 * 
 * Last Verified: December 2025 against NH Administrative Rules
 */

import type { DiagnosticQuestionConfig } from '../../../types/diagnostic';

export const NEWHAMPSHIRE_QUESTIONS: DiagnosticQuestionConfig[] = [
  // ============================================
  // SECTION A: LICENSING & OPERATIONS
  // ============================================
  {
    id: 'NH-A1',
    itemId: 'A1',
    stateCode: 'NH',
    title: 'DHHS Home Health Care Provider License Status',
    description: 'New Hampshire requires all home health care providers to be licensed by DHHS under He-P 809',
    severity: 'high',
    regulationCode: 'He-P 809.04',
    regulationName: 'Licensure',
    agencyName: 'New Hampshire Department of Health and Human Services',
    agencyAbbr: 'DHHS',
    agencyPhone: '(603) 271-9039',
    agencyUrl: 'https://www.dhhs.nh.gov/programs-services/health-care/home-health-care',
    questions: [
      {
        id: 'q1',
        question: 'Do you have a current, active Home Health Care Provider license from NH DHHS?',
        inputType: 'binary',
        options: [
          { value: 'yes', label: 'Yes' },
          { value: 'no', label: 'No' },
        ],
        required: true,
        helpText: 'Per He-P 809.06: Home health care provider licenses are valid for 2 years from date of issuance.',
      },
      {
        id: 'q2',
        question: 'What is your DHHS license expiration date?',
        inputType: 'date',
        helpText: 'Found on your DHHS license certificate. License expires on last day of the month it was issued.',
        showCondition: {
          questionId: 'q1',
          operator: 'equals',
          value: 'yes',
        },
      },
    ],
    scoringRules: [
      { condition: 'equals', questionId: 'q1', value: 'no', result: 'NON_COMPLIANT' },
      { condition: 'daysFuture', questionId: 'q2', value: 120, result: 'COMPLIANT' },
      { condition: 'daysFuture', questionId: 'q2', value: 60, result: 'AT_RISK' },
      { condition: 'daysFuture', questionId: 'q2', value: 0, result: 'NON_COMPLIANT' },
    ],
    reportLanguage: {
      compliant: 'Your NH DHHS Home Health Care Provider license is current and active per He-P 809.',
      atRisk: 'Your DHHS license expires within 120 days. Per He-P 809.06, submit renewal application before expiration to avoid lapse.',
      nonCompliant: 'Operating a home health care agency in New Hampshire without a valid DHHS license violates RSA 151 and He-P 809. Contact DHHS immediately at (603) 271-9039.',
      unknown: 'Verify your DHHS license status by calling (603) 271-9039.',
    },
    stateNotes: [
      'Licenses valid for 2 years per He-P 809.06',
      'License expires on last day of month it was issued',
      'Renewal application must be received before expiration',
      'Inspection required prior to initial license per He-P 809.05(f)',
    ],
  },

  {
    id: 'NH-A2',
    itemId: 'A2',
    stateCode: 'NH',
    title: 'Administrator Qualifications',
    description: 'New Hampshire requires qualified administrator per He-P 809.15',
    severity: 'high',
    regulationCode: 'He-P 809.15(c)',
    regulationName: 'Administrator',
    agencyName: 'New Hampshire Department of Health and Human Services',
    agencyAbbr: 'DHHS',
    agencyPhone: '(603) 271-9039',
    agencyUrl: 'https://www.dhhs.nh.gov/programs-services/health-care/home-health-care',
    questions: [
      {
        id: 'q1',
        question: 'Does your agency have a designated administrator who meets He-P 809.15(d) qualifications?',
        inputType: 'select',
        options: [
          { value: 'qualified', label: 'Yes - Bachelor\'s degree in business or health-related field' },
          { value: 'waiver', label: 'Has waiver from DHHS per He-P 809.10' },
          { value: 'no', label: 'No / Does not meet requirements' },
        ],
        required: true,
        helpText: 'Per He-P 809.15(d): Any new administrator shall possess at least a bachelor\'s degree in business or a health-related field.',
      },
      {
        id: 'q2',
        question: 'Is a Director of Patient Services designated with required qualifications?',
        inputType: 'select',
        options: [
          { value: 'qualified', label: 'Yes - 2+ years supervisory experience per He-P 809.15(f)' },
          { value: 'waiver', label: 'Has waiver from DHHS' },
          { value: 'no', label: 'No / Does not meet requirements' },
        ],
        helpText: 'Per He-P 809.15(f): Director of Patient Services shall have at least 2 years experience supervising personnel.',
      },
    ],
    scoringRules: [
      { condition: 'allTrue', value: [
        { condition: 'includes', questionId: 'q1', value: ['qualified', 'waiver'] },
        { condition: 'includes', questionId: 'q2', value: ['qualified', 'waiver'] },
      ], result: 'COMPLIANT' },
      { condition: 'equals', questionId: 'q1', value: 'no', result: 'NON_COMPLIANT' },
      { condition: 'equals', questionId: 'q2', value: 'no', result: 'AT_RISK' },
    ],
    reportLanguage: {
      compliant: 'Administrator and Director of Patient Services meet He-P 809.15 qualification requirements.',
      atRisk: 'Director of Patient Services qualifications need verification.',
      nonCompliant: 'Administrator does not meet He-P 809.15(d) requirements. Must have bachelor\'s degree or obtain waiver per He-P 809.10.',
      unknown: 'Verify administrator qualifications against He-P 809.15 requirements.',
    },
    stateNotes: [
      'Administrator: Bachelor\'s degree in business or health-related field',
      'Director of Patient Services: 2 years supervisory experience',
      'Waivers available per He-P 809.10 if alternative equally protective',
      'Must report administrator changes per He-P 809.08',
    ],
  },

  {
    id: 'NH-A3',
    itemId: 'A3',
    stateCode: 'NH',
    title: 'Ownership and Administrator Change Notification',
    description: 'New Hampshire requires timely notification of organizational changes',
    severity: 'medium',
    regulationCode: 'He-P 809.08',
    regulationName: 'Changes',
    agencyName: 'New Hampshire Department of Health and Human Services',
    agencyAbbr: 'DHHS',
    agencyPhone: '(603) 271-9039',
    agencyUrl: 'https://www.dhhs.nh.gov/programs-services/health-care/home-health-care',
    questions: [
      {
        id: 'q1',
        question: 'Is your ownership and administrator information current with DHHS?',
        inputType: 'select',
        options: [
          { value: 'current', label: 'Yes, all information is current' },
          { value: 'pending', label: 'Changes pending notification to DHHS' },
          { value: 'outdated', label: 'No, information is outdated' },
        ],
        required: true,
        helpText: 'Per He-P 809.08: Written notice required at least 30 days prior to organizational changes.',
      },
    ],
    scoringRules: [
      { condition: 'equals', value: 'current', result: 'COMPLIANT' },
      { condition: 'equals', value: 'pending', result: 'AT_RISK' },
      { condition: 'equals', value: 'outdated', result: 'NON_COMPLIANT' },
    ],
    reportLanguage: {
      compliant: 'Ownership and administrator information is current with DHHS.',
      atRisk: 'Notify DHHS of organizational changes immediately per He-P 809.08.',
      nonCompliant: 'Outdated information violates He-P 809.08. Update DHHS within 30 days of any changes.',
      unknown: 'Verify your information is current with DHHS.',
    },
    stateNotes: [
      '30 days written notice required before organizational changes',
      'Ownership changes require inspection per He-P 809.08(f)',
      'Administrator changes require resume, license copies, and background check',
      'Contact DHHS at (603) 271-9039 for change notifications',
    ],
  },

  {
    id: 'NH-A4',
    itemId: 'A4',
    stateCode: 'NH',
    title: 'Written Policies and Procedures',
    description: 'New Hampshire requires specific written policies per He-P 809.14',
    severity: 'high',
    regulationCode: 'He-P 809.14(b)',
    regulationName: 'Policies',
    agencyName: 'New Hampshire Department of Health and Human Services',
    agencyAbbr: 'DHHS',
    agencyPhone: '(603) 271-9039',
    agencyUrl: 'https://www.dhhs.nh.gov/programs-services/health-care/home-health-care',
    questions: [
      {
        id: 'q1',
        question: 'Which of the following New Hampshire-required policies does your agency have documented?',
        inputType: 'checklist',
        options: [
          { value: 'patient_rights', label: 'Patient rights per RSA 151:21-b (Home Care Clients\' Bill of Rights)' },
          { value: 'scope_services', label: 'Scope and type of services per He-P 809.14(c)' },
          { value: 'admission_discharge', label: 'Admission and discharge criteria' },
          { value: 'supervision', label: 'Clinical supervision requirements' },
          { value: 'complaint', label: 'Complaint and grievance procedures' },
          { value: 'background', label: 'Criminal background check procedures per RSA 151:2-d' },
          { value: 'training', label: 'Staff training requirements per He-P 809.17' },
          { value: 'infection_control', label: 'Infection control policies per He-P 809.19' },
          { value: 'abuse_reporting', label: 'Abuse and neglect reporting per RSA 161-F:46' },
          { value: 'emergency', label: 'Emergency preparedness per He-P 809.20' },
        ],
        required: true,
        helpText: 'Per He-P 809.14(b): The licensee shall have written policies and procedures.',
      },
    ],
    scoringRules: [
      { condition: 'countAtLeast', value: 10, result: 'COMPLIANT' },
      { condition: 'countAtLeast', value: 7, result: 'AT_RISK' },
      { condition: 'countLessThan', value: 7, result: 'NON_COMPLIANT' },
    ],
    reportLanguage: {
      compliant: 'Your policy manual meets New Hampshire He-P 809.14 requirements.',
      atRisk: 'Your policy manual is missing required sections. DHHS surveyors will review policies for completeness.',
      nonCompliant: 'Multiple required policies are missing per He-P 809.14. Immediately develop written policies for all required areas.',
      unknown: 'Review your policies against He-P 809.14 requirements.',
    },
    stateNotes: [
      'Written policies and procedures required per He-P 809.14(b)',
      'Must comply with all federal, state, and local laws per He-P 809.14(a)',
      'Policies reviewed during DHHS inspections',
      'Include all required topics from He-P 809.14',
      'Review policies annually and update as needed',
    ],
  },

  {
    id: 'NH-A5',
    itemId: 'A5',
    stateCode: 'NH',
    title: 'Record Retention',
    description: 'New Hampshire requires 7 years minimum record retention',
    severity: 'medium',
    regulationCode: 'RSA 151:9',
    regulationName: 'Retention',
    agencyName: 'New Hampshire Department of Health and Human Services',
    agencyAbbr: 'DHHS',
    agencyPhone: '(603) 271-9039',
    agencyUrl: 'https://www.dhhs.nh.gov/programs-services/health-care/home-health-care',
    questions: [
      {
        id: 'q1',
        question: 'How long do you retain patient and employee records?',
        inputType: 'select',
        options: [
          { value: '7plus', label: '7 years or longer (meets NH requirement)' },
          { value: '5to6', label: '5-6 years' },
          { value: 'less5', label: 'Less than 5 years' },
          { value: 'unsure', label: 'Not sure / no formal policy' },
        ],
        required: true,
        helpText: 'New Hampshire requires minimum 7-year record retention for health care providers.',
      },
    ],
    scoringRules: [
      { condition: 'equals', value: '7plus', result: 'COMPLIANT' },
      { condition: 'equals', value: '5to6', result: 'AT_RISK' },
      { condition: 'includes', value: ['less5', 'unsure'], result: 'NON_COMPLIANT' },
    ],
    reportLanguage: {
      compliant: 'Your record retention policy meets New Hampshire\'s 7-year requirement.',
      atRisk: 'New Hampshire requires 7-year record retention. Update your policy to ensure compliance.',
      nonCompliant: 'Your record retention does not meet New Hampshire\'s 7-year requirement. Immediately update your retention policy.',
      unknown: 'Review record retention requirements.',
    },
    stateNotes: [
      'Minimum 7-year retention for patient records',
      'Minors: retain until age 19 or 7 years, whichever is longer',
      'Must arrange for record storage if agency ceases operation',
    ],
  },

  {
    id: 'NH-A6',
    itemId: 'A6',
    stateCode: 'NH',
    title: 'Contracted Services Requirements',
    description: 'New Hampshire requires written agreements for services provided by arrangement',
    severity: 'medium',
    regulationCode: 'He-P 809.15(a)',
    regulationName: 'Contracts',
    agencyName: 'New Hampshire Department of Health and Human Services',
    agencyAbbr: 'DHHS',
    agencyPhone: '(603) 271-9039',
    agencyUrl: 'https://www.dhhs.nh.gov/programs-services/health-care/home-health-care',
    questions: [
      {
        id: 'q1',
        question: 'Does your agency provide any services through arrangement with another entity?',
        inputType: 'binary',
        options: [
          { value: 'yes', label: 'Yes' },
          { value: 'no', label: 'No - all services provided by agency employees' },
        ],
        required: true,
      },
      {
        id: 'q2',
        question: 'Do ALL arranged services have written contracts meeting He-P 809.15(b) requirements?',
        inputType: 'binary',
        options: [
          { value: 'yes', label: 'Yes - contracts specify services, qualifications, HHCP authorization, and professional responsibility' },
          { value: 'no', label: 'No' },
        ],
        showCondition: {
          questionId: 'q1',
          operator: 'equals',
          value: 'yes',
        },
      },
    ],
    scoringRules: [
      { condition: 'equals', questionId: 'q1', value: 'no', result: 'COMPLIANT' },
      { condition: 'allTrue', value: [
        { condition: 'equals', questionId: 'q1', value: 'yes' },
        { condition: 'equals', questionId: 'q2', value: 'yes' },
      ], result: 'COMPLIANT' },
      { condition: 'equals', questionId: 'q2', value: 'no', result: 'NON_COMPLIANT' },
    ],
    reportLanguage: {
      compliant: 'Contracted services meet He-P 809.15(a)-(b) requirements.',
      atRisk: 'Review contracts for arranged services.',
      nonCompliant: 'Arranged services must have written contracts per He-P 809.15(a)-(b). Contracts must specify services, qualifications, HHCP authorization, and that HHCP retains professional responsibility.',
      unknown: 'Verify contract documentation.',
    },
    stateNotes: [
      'Written contractual agreement required for all arranged services',
      'Contract must identify services to be provided',
      'Contract must specify personnel qualifications',
      'HHCP retains professional responsibility for all care',
      'Services must be authorized by HHCP per He-P 809.15(b)',
    ],
  },

  // ============================================
  // SECTION B: PERSONNEL & BACKGROUND CHECKS
  // ============================================
  {
    id: 'NH-B1',
    itemId: 'B1',
    stateCode: 'NH',
    title: 'Criminal Background Check Requirements',
    description: 'New Hampshire requires criminal background checks per RSA 151:2-d',
    severity: 'high',
    regulationCode: 'RSA 151:2-d',
    regulationName: 'Background Checks',
    agencyName: 'New Hampshire Department of Health and Human Services',
    agencyAbbr: 'DHHS',
    agencyPhone: '(603) 271-9039',
    agencyUrl: 'https://www.dhhs.nh.gov/programs-services/health-care/home-health-care',
    questions: [
      {
        id: 'q1',
        question: 'For ALL staff with patient contact, which background checks do you complete BEFORE patient contact?',
        inputType: 'checklist',
        options: [
          { value: 'state_check', label: 'NH State Police criminal records check' },
          { value: 'fbi', label: 'FBI fingerprint-based background check' },
          { value: 'sex_offender', label: 'Sex offender registry check' },
          { value: 'abuse_registry', label: 'State abuse/neglect registry check' },
          { value: 'nurse_aide', label: 'NH Nurse Aide Registry check (if applicable)' },
        ],
        required: true,
        helpText: 'Per RSA 151:2-d and He-P 809.04(g): Criminal records check results must be available for inspection.',
      },
      {
        id: 'q2',
        question: 'Do any staff begin working with patients BEFORE their background check clearance is received?',
        inputType: 'binary',
        options: [
          { value: 'no', label: 'No - never before clearance' },
          { value: 'yes', label: 'Yes - sometimes they start early' },
        ],
        required: true,
      },
    ],
    scoringRules: [
      { condition: 'allTrue', value: [
        { condition: 'countAtLeast', questionId: 'q1', value: 4 },
        { condition: 'equals', questionId: 'q2', value: 'no' },
      ], result: 'COMPLIANT' },
      { condition: 'equals', questionId: 'q2', value: 'yes', result: 'NON_COMPLIANT' },
      { condition: 'countAtLeast', questionId: 'q1', value: 2, result: 'AT_RISK' },
      { condition: 'countLessThan', questionId: 'q1', value: 2, result: 'NON_COMPLIANT' },
    ],
    reportLanguage: {
      compliant: 'Background check procedures meet RSA 151:2-d and He-P 809.17 requirements.',
      atRisk: 'Background check documentation may be incomplete.',
      nonCompliant: 'Staff working before background check clearance or without proper screening violates RSA 151:2-d.',
      unknown: 'Audit all staff files for background check documentation.',
    },
    stateNotes: [
      'Criminal records check from NH Department of Safety required',
      'Results must be available at initial inspection per He-P 809.04(g)',
      'Cannot employ persons with certain convictions per He-P 809.17',
      'Waiver available per He-P 809.17 if no threat to health/safety',
    ],
  },

  {
    id: 'NH-B2',
    itemId: 'B2',
    stateCode: 'NH',
    title: 'Personnel File Requirements',
    description: 'New Hampshire requires specific documents in each employee\'s personnel file',
    severity: 'high',
    regulationCode: 'He-P 809.17',
    regulationName: 'Personnel',
    agencyName: 'New Hampshire Department of Health and Human Services',
    agencyAbbr: 'DHHS',
    agencyPhone: '(603) 271-9039',
    agencyUrl: 'https://www.dhhs.nh.gov/programs-services/health-care/home-health-care',
    questions: [
      {
        id: 'q1',
        question: 'Which documents do you maintain in EVERY employee\'s personnel file?',
        inputType: 'checklist',
        options: [
          { value: 'app', label: 'Employment application with work history' },
          { value: 'id', label: 'Government-issued photo ID' },
          { value: 'license', label: 'Professional license verification' },
          { value: 'background', label: 'Criminal background check clearance' },
          { value: 'references', label: 'Reference verification' },
          { value: 'health', label: 'Health clearance/physical exam' },
          { value: 'tb', label: 'TB screening documentation' },
          { value: 'i9', label: 'I-9 Employment Eligibility Verification' },
          { value: 'orientation', label: 'Orientation and training documentation' },
          { value: 'job_description', label: 'Job description' },
        ],
        required: true,
        helpText: 'Per He-P 809.17: Complete personnel files must be maintained.',
      },
    ],
    scoringRules: [
      { condition: 'countAtLeast', value: 10, result: 'COMPLIANT' },
      { condition: 'countAtLeast', value: 7, result: 'AT_RISK' },
      { condition: 'countLessThan', value: 7, result: 'NON_COMPLIANT' },
    ],
    reportLanguage: {
      compliant: 'Personnel files contain required documentation per He-P 809.17.',
      atRisk: 'Some personnel files are missing required documents.',
      nonCompliant: 'Personnel files are missing critical documentation required by He-P 809.17.',
      unknown: 'Audit personnel files against He-P 809.17 requirements.',
    },
    stateNotes: [
      'Job descriptions required for each position per He-P 809.17',
      'Background checks must be complete before patient contact',
      'Files reviewed during DHHS inspections',
    ],
  },

  {
    id: 'NH-B3',
    itemId: 'B3',
    stateCode: 'NH',
    title: 'Professional License Verification',
    description: 'New Hampshire requires verification of all professional licenses',
    severity: 'high',
    regulationCode: 'He-P 809.17',
    regulationName: 'Licenses',
    agencyName: 'NH Office of Professional Licensure and Certification',
    agencyAbbr: 'OPLC',
    agencyPhone: '(603) 271-2152',
    agencyUrl: 'https://www.oplc.nh.gov/',
    questions: [
      {
        id: 'q1',
        question: 'Do you verify professional licenses with NH OPLC or appropriate licensing boards before hire and track renewals?',
        inputType: 'select',
        options: [
          { value: 'always', label: 'Yes, verify before hire and track renewals' },
          { value: 'hire_only', label: 'Verify at hire only' },
          { value: 'no', label: 'No formal verification process' },
        ],
        required: true,
        helpText: 'NH OPLC maintains license records at www.oplc.nh.gov',
      },
    ],
    scoringRules: [
      { condition: 'equals', value: 'always', result: 'COMPLIANT' },
      { condition: 'equals', value: 'hire_only', result: 'AT_RISK' },
      { condition: 'equals', value: 'no', result: 'NON_COMPLIANT' },
    ],
    reportLanguage: {
      compliant: 'Professional license verification process is comprehensive and ongoing.',
      atRisk: 'License verification at hire only is insufficient. Implement ongoing verification to catch expired licenses.',
      nonCompliant: 'No license verification process creates significant risk. Implement verification with NH licensing boards immediately.',
      unknown: 'Verify license verification procedures are in place.',
    },
    stateNotes: [
      'Verify all professional licenses before hire',
      'Track renewal dates',
      'NH Office of Professional Licensure: (603) 271-2152',
      'Verify at www.oplc.nh.gov',
      'Document verification in personnel file',
    ],
  },

  {
    id: 'NH-B4',
    itemId: 'B4',
    stateCode: 'NH',
    title: 'OIG/SAM Exclusion List Verification',
    description: 'Federal exclusion list verification required for all employees',
    severity: 'high',
    regulationCode: '42 CFR 1001.1901',
    regulationName: 'OIG Exclusions',
    agencyName: 'Office of Inspector General',
    agencyAbbr: 'OIG',
    agencyPhone: '(800) 447-8477',
    agencyUrl: 'https://oig.hhs.gov/exclusions/',
    questions: [
      {
        id: 'q1',
        question: 'How often do you check the OIG exclusion list for employees?',
        inputType: 'select',
        options: [
          { value: 'monthly', label: 'Monthly' },
          { value: 'hire_only', label: 'At hire only' },
          { value: 'no', label: 'Not checking' },
        ],
        required: true,
      },
      {
        id: 'q2',
        question: 'Do you check SAM.gov exclusions?',
        inputType: 'binary',
        options: [
          { value: 'yes', label: 'Yes' },
          { value: 'no', label: 'No' },
        ],
      },
    ],
    scoringRules: [
      { condition: 'allTrue', value: [
        { condition: 'equals', questionId: 'q1', value: 'monthly' },
        { condition: 'equals', questionId: 'q2', value: 'yes' },
      ], result: 'COMPLIANT' },
      { condition: 'equals', questionId: 'q1', value: 'hire_only', result: 'AT_RISK' },
      { condition: 'equals', questionId: 'q1', value: 'no', result: 'NON_COMPLIANT' },
    ],
    reportLanguage: {
      compliant: 'OIG/SAM exclusion verification is performed regularly.',
      atRisk: 'Implement monthly OIG/SAM verification. At-hire only is insufficient.',
      nonCompliant: 'Employing excluded individuals can result in civil monetary penalties.',
      unknown: 'Verify OIG/SAM checking procedures.',
    },
    stateNotes: [
      'OIG recommends monthly verification',
      'SAM.gov verification also recommended',
      'Document all exclusion checks',
      'OIG LEIE: https://exclusions.oig.hhs.gov',
      'SAM.gov: https://sam.gov',
    ],
  },

  // ============================================
  // SECTION C: HEALTH & SAFETY
  // ============================================
  {
    id: 'NH-C1',
    itemId: 'C1',
    stateCode: 'NH',
    title: 'TB Screening Requirements',
    description: 'New Hampshire requires TB screening for all staff with patient contact',
    severity: 'high',
    regulationCode: 'He-P 809.17',
    regulationName: 'TB Screening',
    agencyName: 'New Hampshire Department of Health and Human Services',
    agencyAbbr: 'DHHS',
    agencyPhone: '(603) 271-9039',
    agencyUrl: 'https://www.dhhs.nh.gov/programs-services/health-care/home-health-care',
    questions: [
      {
        id: 'q1',
        question: 'Do ALL staff with patient contact have current TB screening documentation on file?',
        inputType: 'select',
        options: [
          { value: 'all', label: 'Yes, all staff have current TB screening' },
          { value: 'most', label: 'Most do, some may be missing' },
          { value: 'no', label: 'No / Not tracking this' },
        ],
        required: true,
        helpText: 'TB screening must be completed BEFORE patient contact.',
      },
      {
        id: 'q2',
        question: 'How do you track TB screening due dates?',
        inputType: 'select',
        options: [
          { value: 'system', label: 'Automated system with reminders' },
          { value: 'manual', label: 'Manual tracking (spreadsheet, calendar)' },
          { value: 'none', label: 'No active tracking system' },
        ],
      },
    ],
    scoringRules: [
      { condition: 'allTrue', value: [
        { condition: 'equals', questionId: 'q1', value: 'all' },
        { condition: 'includes', questionId: 'q2', value: ['system', 'manual'] },
      ], result: 'COMPLIANT' },
      { condition: 'anyTrue', value: [
        { condition: 'equals', questionId: 'q1', value: 'most' },
        { condition: 'equals', questionId: 'q2', value: 'none' },
      ], result: 'AT_RISK' },
      { condition: 'equals', questionId: 'q1', value: 'no', result: 'NON_COMPLIANT' },
    ],
    reportLanguage: {
      compliant: 'TB screening documentation is current for all staff.',
      atRisk: 'Some TB screenings may be missing. Review all staff files.',
      nonCompliant: 'Staff are working without valid TB screening. This creates health risks.',
      unknown: 'Verify TB screening status for all staff.',
    },
    stateNotes: [
      'TB screening required before patient contact',
      'Track renewal dates',
      'Document all screenings in personnel file',
    ],
  },

  {
    id: 'NH-C2',
    itemId: 'C2',
    stateCode: 'NH',
    title: 'Infection Control Program',
    description: 'New Hampshire requires a comprehensive infection control program per He-P 809.19',
    severity: 'high',
    regulationCode: 'He-P 809.19',
    regulationName: 'Infection Control',
    agencyName: 'New Hampshire Department of Health and Human Services',
    agencyAbbr: 'DHHS',
    agencyPhone: '(603) 271-9039',
    agencyUrl: 'https://www.dhhs.nh.gov/programs-services/health-care/home-health-care',
    questions: [
      {
        id: 'q1',
        question: 'Which infection control components does your agency have in place?',
        inputType: 'checklist',
        options: [
          { value: 'policy', label: 'Written infection control policies' },
          { value: 'training', label: 'Staff infection control training' },
          { value: 'ppe', label: 'PPE availability and training' },
          { value: 'hand_hygiene', label: 'Hand hygiene protocols' },
          { value: 'surveillance', label: 'Infection surveillance and reporting' },
          { value: 'waste', label: 'Medical waste disposal procedures' },
        ],
        required: true,
        helpText: 'Per He-P 809.19: Licensee shall develop and implement infection control program.',
      },
    ],
    scoringRules: [
      { condition: 'countAtLeast', value: 6, result: 'COMPLIANT' },
      { condition: 'countAtLeast', value: 4, result: 'AT_RISK' },
      { condition: 'countLessThan', value: 4, result: 'NON_COMPLIANT' },
    ],
    reportLanguage: {
      compliant: 'Infection control program meets He-P 809.19 requirements.',
      atRisk: 'Infection control program is missing some components.',
      nonCompliant: 'Infection control program is inadequate per He-P 809.19.',
      unknown: 'Review infection control program against He-P 809.19.',
    },
    stateNotes: [
      'Written policies and procedures required',
      'Staff training required',
      'PPE availability required',
      'Infection surveillance required',
      'Develop and implement per He-P 809.19',
    ],
  },

  {
    id: 'NH-C3',
    itemId: 'C3',
    stateCode: 'NH',
    title: 'Emergency Preparedness Plan',
    description: 'New Hampshire requires emergency preparedness per He-P 809.20',
    severity: 'high',
    regulationCode: 'He-P 809.20',
    regulationName: 'Emergency',
    agencyName: 'New Hampshire Department of Health and Human Services',
    agencyAbbr: 'DHHS',
    agencyPhone: '(603) 271-9039',
    agencyUrl: 'https://www.dhhs.nh.gov/programs-services/health-care/home-health-care',
    questions: [
      {
        id: 'q1',
        question: 'Does your agency have a comprehensive emergency preparedness plan that includes:',
        inputType: 'checklist',
        options: [
          { value: 'plan', label: 'Written emergency/disaster plan' },
          { value: 'weather', label: 'Severe weather procedures (winter storms, flooding)' },
          { value: 'contact', label: 'Patient emergency contact procedures' },
          { value: 'communication', label: 'Staff communication plan' },
          { value: 'training', label: 'Staff training on emergency procedures' },
          { value: 'continuity', label: 'Service continuity during emergencies' },
        ],
        required: true,
        helpText: 'Per He-P 809.20: Licensee shall develop written emergency plan.',
      },
    ],
    scoringRules: [
      { condition: 'countAtLeast', value: 6, result: 'COMPLIANT' },
      { condition: 'countAtLeast', value: 4, result: 'AT_RISK' },
      { condition: 'countLessThan', value: 4, result: 'NON_COMPLIANT' },
    ],
    reportLanguage: {
      compliant: 'Emergency preparedness plan is comprehensive per He-P 809.20.',
      atRisk: 'Emergency preparedness plan is incomplete.',
      nonCompliant: 'Emergency preparedness plan is inadequate per He-P 809.20.',
      unknown: 'Review emergency preparedness requirements.',
    },
    stateNotes: [
      'Written emergency plan required',
      'Address NH-specific weather emergencies (winter storms)',
      'Train staff on emergency procedures',
      'Maintain service continuity',
    ],
  },

  // ============================================
  // SECTION D: TRAINING & COMPETENCY
  // ============================================
  {
    id: 'NH-D1',
    itemId: 'D1',
    stateCode: 'NH',
    title: 'Orientation Training',
    description: 'New Hampshire requires comprehensive orientation before patient contact',
    severity: 'high',
    regulationCode: 'He-P 809.17',
    regulationName: 'Orientation',
    agencyName: 'New Hampshire Department of Health and Human Services',
    agencyAbbr: 'DHHS',
    agencyPhone: '(603) 271-9039',
    agencyUrl: 'https://www.dhhs.nh.gov/programs-services/health-care/home-health-care',
    questions: [
      {
        id: 'q1',
        question: 'Do ALL new staff complete the following BEFORE any patient contact?',
        inputType: 'checklist',
        options: [
          { value: 'orientation', label: 'General agency orientation' },
          { value: 'policies', label: 'Review of agency policies and procedures' },
          { value: 'safety', label: 'Safety and emergency procedures training' },
          { value: 'infection', label: 'Infection control training per He-P 809.19' },
          { value: 'hipaa', label: 'HIPAA and confidentiality training' },
          { value: 'abuse', label: 'Abuse recognition and reporting per RSA 161-F:46' },
          { value: 'patient_rights', label: 'Patient rights per RSA 151:21-b' },
        ],
        required: true,
        helpText: 'Per He-P 809.17: Staff must be oriented before patient contact.',
      },
    ],
    scoringRules: [
      { condition: 'countAtLeast', value: 7, result: 'COMPLIANT' },
      { condition: 'countAtLeast', value: 5, result: 'AT_RISK' },
      { condition: 'countLessThan', value: 5, result: 'NON_COMPLIANT' },
    ],
    reportLanguage: {
      compliant: 'Orientation training program meets He-P 809.17 requirements.',
      atRisk: 'Orientation training may be incomplete.',
      nonCompliant: 'Staff are providing services without required orientation training.',
      unknown: 'Review orientation training program.',
    },
    stateNotes: [
      'Orientation before patient contact',
      'Document all training in personnel file',
      'Include all required topics',
    ],
  },

  {
    id: 'NH-D2',
    itemId: 'D2',
    stateCode: 'NH',
    title: 'Abuse Recognition and Reporting Training',
    description: 'New Hampshire requires mandatory reporting training per RSA 161-F:46',
    severity: 'high',
    regulationCode: 'RSA 161-F:46',
    regulationName: 'Abuse Reporting',
    agencyName: 'NH Bureau of Elderly and Adult Services',
    agencyAbbr: 'BEAS',
    agencyPhone: '(800) 949-0470',
    agencyUrl: 'https://www.dhhs.nh.gov/programs-services/adult-aging-care/protective-services',
    questions: [
      {
        id: 'q1',
        question: 'Do ALL staff receive training on identifying and reporting suspected abuse, neglect, or exploitation?',
        inputType: 'select',
        options: [
          { value: 'orientation_annual', label: 'Yes, during orientation and annually' },
          { value: 'orientation_only', label: 'Yes, during orientation only' },
          { value: 'no', label: 'No formal training' },
        ],
        required: true,
        helpText: 'Per RSA 161-F:46: Healthcare workers are mandatory reporters of suspected abuse.',
      },
      {
        id: 'q2',
        question: 'Do staff know how to report to NH Bureau of Elderly and Adult Services?',
        inputType: 'binary',
        options: [
          { value: 'yes', label: 'Yes, reporting procedures are documented and trained' },
          { value: 'no', label: 'No / not sure' },
        ],
        required: true,
        helpText: 'BEAS Hotline: (800) 949-0470',
      },
    ],
    scoringRules: [
      { condition: 'allTrue', value: [
        { condition: 'equals', questionId: 'q1', value: 'orientation_annual' },
        { condition: 'equals', questionId: 'q2', value: 'yes' },
      ], result: 'COMPLIANT' },
      { condition: 'anyTrue', value: [
        { condition: 'equals', questionId: 'q1', value: 'orientation_only' },
        { condition: 'equals', questionId: 'q2', value: 'no' },
      ], result: 'AT_RISK' },
      { condition: 'equals', questionId: 'q1', value: 'no', result: 'NON_COMPLIANT' },
    ],
    reportLanguage: {
      compliant: 'Staff are properly trained on mandatory abuse reporting per RSA 161-F:46.',
      atRisk: 'Abuse reporting training may be insufficient. Ensure ongoing training.',
      nonCompliant: 'Failure to train on mandatory abuse reporting violates RSA 161-F:46.',
      unknown: 'Verify abuse reporting training.',
    },
    stateNotes: [
      'Healthcare workers are mandatory reporters',
      'BEAS Hotline: (800) 949-0470',
      'Train at orientation and annually',
      'Document all training',
    ],
  },

  {
    id: 'NH-D3',
    itemId: 'D3',
    stateCode: 'NH',
    title: 'Competency Evaluation',
    description: 'New Hampshire requires documented competency evaluations',
    severity: 'high',
    regulationCode: 'He-P 809.17',
    regulationName: 'Competency',
    agencyName: 'New Hampshire Department of Health and Human Services',
    agencyAbbr: 'DHHS',
    agencyPhone: '(603) 271-9039',
    agencyUrl: 'https://www.dhhs.nh.gov/programs-services/health-care/home-health-care',
    questions: [
      {
        id: 'q1',
        question: 'Do you complete documented competency evaluations for all clinical staff?',
        inputType: 'select',
        options: [
          { value: 'initial_annual', label: 'Yes, at hire and annually' },
          { value: 'initial_only', label: 'Yes, at hire only' },
          { value: 'no', label: 'No formal competency evaluations' },
        ],
        required: true,
      },
      {
        id: 'q2',
        question: 'Which areas are included in competency evaluations?',
        inputType: 'checklist',
        options: [
          { value: 'clinical_skills', label: 'Clinical skills assessment' },
          { value: 'equipment', label: 'Equipment operation' },
          { value: 'documentation', label: 'Documentation accuracy' },
          { value: 'communication', label: 'Patient communication' },
          { value: 'emergency', label: 'Emergency response' },
        ],
      },
    ],
    scoringRules: [
      { condition: 'allTrue', value: [
        { condition: 'equals', questionId: 'q1', value: 'initial_annual' },
        { condition: 'countAtLeast', questionId: 'q2', value: 4 },
      ], result: 'COMPLIANT' },
      { condition: 'anyTrue', value: [
        { condition: 'equals', questionId: 'q1', value: 'initial_only' },
        { condition: 'countAtLeast', questionId: 'q2', value: 2 },
      ], result: 'AT_RISK' },
      { condition: 'equals', questionId: 'q1', value: 'no', result: 'NON_COMPLIANT' },
    ],
    reportLanguage: {
      compliant: 'Competency evaluation program meets requirements.',
      atRisk: 'Competency evaluations may be incomplete. Implement annual evaluations.',
      nonCompliant: 'No competency evaluation program exists.',
      unknown: 'Review competency evaluation program.',
    },
    stateNotes: [
      'Competency evaluations at hire and annually',
      'Cover all relevant clinical skills',
      'Document in personnel file',
      'Include job-specific competencies',
      'Evaluate by qualified supervisor',
    ],
  },

  // ============================================
  // SECTION E: CARE DELIVERY
  // ============================================
  {
    id: 'NH-E1',
    itemId: 'E1',
    stateCode: 'NH',
    title: 'Plan of Care Requirements',
    description: 'New Hampshire requires comprehensive plans of care per He-P 809.15(o)-(q)',
    severity: 'high',
    regulationCode: 'He-P 809.15(o)',
    regulationName: 'Plan of Care',
    agencyName: 'New Hampshire Department of Health and Human Services',
    agencyAbbr: 'DHHS',
    agencyPhone: '(603) 271-9039',
    agencyUrl: 'https://www.dhhs.nh.gov/programs-services/health-care/home-health-care',
    questions: [
      {
        id: 'q1',
        question: 'Do you have a written care plan for EVERY patient before services begin?',
        inputType: 'select',
        options: [
          { value: 'always', label: 'Yes, always before first visit' },
          { value: 'usually', label: 'Usually, but sometimes services start first' },
          { value: 'no', label: 'No formal care plan process' },
        ],
        required: true,
        helpText: 'Per He-P 809.15(o)-(q): Care plan must be developed in consultation with patient.',
      },
      {
        id: 'q2',
        question: 'Do your care plans include all required elements?',
        inputType: 'checklist',
        options: [
          { value: 'assessment', label: 'Patient assessment findings' },
          { value: 'diagnosis', label: 'Primary diagnosis and relevant medical history' },
          { value: 'services', label: 'Specific services to be provided' },
          { value: 'frequency', label: 'Frequency and duration of services' },
          { value: 'goals', label: 'Patient goals and outcomes' },
          { value: 'physician', label: 'Physician orders (if applicable)' },
          { value: 'patient_input', label: 'Developed in consultation with patient per He-P 809.15(k)' },
        ],
      },
    ],
    scoringRules: [
      { condition: 'allTrue', value: [
        { condition: 'equals', questionId: 'q1', value: 'always' },
        { condition: 'countAtLeast', questionId: 'q2', value: 6 },
      ], result: 'COMPLIANT' },
      { condition: 'anyTrue', value: [
        { condition: 'equals', questionId: 'q1', value: 'usually' },
        { condition: 'countAtLeast', questionId: 'q2', value: 4 },
      ], result: 'AT_RISK' },
      { condition: 'equals', questionId: 'q1', value: 'no', result: 'NON_COMPLIANT' },
    ],
    reportLanguage: {
      compliant: 'Care plans are completed before services begin and include required elements per He-P 809.15(o)-(q).',
      atRisk: 'Care plans may be incomplete or created after services begin.',
      nonCompliant: 'Services are being provided without written care plans. This violates He-P 809.15.',
      unknown: 'Review care plan process.',
    },
    stateNotes: [
      'Care plan required before services begin',
      'Developed in consultation with patient per He-P 809.15(k)',
      'Must include all required elements',
      'Review and update as patient needs change',
    ],
  },

  {
    id: 'NH-E2',
    itemId: 'E2',
    stateCode: 'NH',
    title: 'Patient Rights',
    description: 'New Hampshire requires providing patient rights per RSA 151:21-b',
    severity: 'high',
    regulationCode: 'RSA 151:21-b',
    regulationName: 'Rights',
    agencyName: 'New Hampshire Department of Health and Human Services',
    agencyAbbr: 'DHHS',
    agencyPhone: '(603) 271-9039',
    agencyUrl: 'https://www.dhhs.nh.gov/programs-services/health-care/home-health-care',
    questions: [
      {
        id: 'q1',
        question: 'Do you provide written patient rights per RSA 151:21-b to all patients at admission?',
        inputType: 'binary',
        options: [
          { value: 'yes', label: 'Yes' },
          { value: 'no', label: 'No' },
        ],
        required: true,
        helpText: 'Per He-P 809.14(b)(1): Written policies must include rights and responsibilities per RSA 151:21-b.',
      },
      {
        id: 'q2',
        question: 'Do patients sign acknowledgment of receiving patient rights?',
        inputType: 'binary',
        options: [
          { value: 'yes', label: 'Yes' },
          { value: 'no', label: 'No' },
        ],
        showCondition: {
          questionId: 'q1',
          operator: 'equals',
          value: 'yes',
        },
      },
    ],
    scoringRules: [
      { condition: 'equals', questionId: 'q1', value: 'no', result: 'NON_COMPLIANT' },
      { condition: 'allTrue', value: [
        { condition: 'equals', questionId: 'q1', value: 'yes' },
        { condition: 'equals', questionId: 'q2', value: 'yes' },
      ], result: 'COMPLIANT' },
      { condition: 'equals', questionId: 'q2', value: 'no', result: 'AT_RISK' },
    ],
    reportLanguage: {
      compliant: 'Patient rights are provided and documented per RSA 151:21-b.',
      atRisk: 'Patient rights acknowledgment is not documented.',
      nonCompliant: 'Patient rights are not being provided. This violates RSA 151:21-b and He-P 809.14(b)(1).',
      unknown: 'Verify patient rights process.',
    },
    stateNotes: [
      'Provide Home Care Clients\' Bill of Rights per RSA 151:21-b',
      'Obtain signed acknowledgment',
      'Include complaint procedures',
    ],
  },

  {
    id: 'NH-E3',
    itemId: 'E3',
    stateCode: 'NH',
    title: 'Service Agreements',
    description: 'New Hampshire requires written service agreements with patients',
    severity: 'medium',
    regulationCode: 'He-P 809.14',
    regulationName: 'Agreements',
    agencyName: 'New Hampshire Department of Health and Human Services',
    agencyAbbr: 'DHHS',
    agencyPhone: '(603) 271-9039',
    agencyUrl: 'https://www.dhhs.nh.gov/programs-services/health-care/home-health-care',
    questions: [
      {
        id: 'q1',
        question: 'Do you have current, signed service agreements with ALL patients?',
        inputType: 'select',
        options: [
          { value: 'all', label: 'Yes, all patients have signed current agreements' },
          { value: 'most', label: 'Most patients have signed agreements' },
          { value: 'no', label: 'No formal service agreements in place' },
        ],
        required: true,
      },
    ],
    scoringRules: [
      { condition: 'equals', value: 'all', result: 'COMPLIANT' },
      { condition: 'equals', value: 'most', result: 'AT_RISK' },
      { condition: 'equals', value: 'no', result: 'NON_COMPLIANT' },
    ],
    reportLanguage: {
      compliant: 'Patient service agreements are current and properly executed.',
      atRisk: 'Some patient service agreements may be missing.',
      nonCompliant: 'Patient service agreements are missing.',
      unknown: 'Verify all patient service agreements are current.',
    },
    stateNotes: [
      'Written service agreements required',
      'Include services, fees, terms',
      'Obtain patient signature',
    ],
  },

  // ============================================
  // SECTION F: QUALITY & DOCUMENTATION
  // ============================================
  {
    id: 'NH-F1',
    itemId: 'F1',
    stateCode: 'NH',
    title: 'Quality Improvement Program',
    description: 'New Hampshire requires quality improvement program per He-P 809.18',
    severity: 'medium',
    regulationCode: 'He-P 809.18',
    regulationName: 'QI Program',
    agencyName: 'New Hampshire Department of Health and Human Services',
    agencyAbbr: 'DHHS',
    agencyPhone: '(603) 271-9039',
    agencyUrl: 'https://www.dhhs.nh.gov/programs-services/health-care/home-health-care',
    questions: [
      {
        id: 'q1',
        question: 'Do you have a documented quality improvement program?',
        inputType: 'binary',
        options: [
          { value: 'yes', label: 'Yes' },
          { value: 'no', label: 'No' },
        ],
        required: true,
        helpText: 'Per He-P 809.18: Licensee shall develop and implement quality improvement program.',
      },
      {
        id: 'q2',
        question: 'Which QI activities does your program include?',
        inputType: 'checklist',
        options: [
          { value: 'chart_review', label: 'Regular clinical record review' },
          { value: 'patient_satisfaction', label: 'Patient satisfaction surveys' },
          { value: 'incident_tracking', label: 'Incident tracking and analysis' },
          { value: 'performance_improvement', label: 'Performance improvement initiatives' },
          { value: 'committee', label: 'QI committee meetings' },
        ],
        showCondition: {
          questionId: 'q1',
          operator: 'equals',
          value: 'yes',
        },
      },
    ],
    scoringRules: [
      { condition: 'equals', questionId: 'q1', value: 'no', result: 'NON_COMPLIANT' },
      { condition: 'countAtLeast', questionId: 'q2', value: 4, result: 'COMPLIANT' },
      { condition: 'countAtLeast', questionId: 'q2', value: 2, result: 'AT_RISK' },
      { condition: 'countLessThan', questionId: 'q2', value: 2, result: 'NON_COMPLIANT' },
    ],
    reportLanguage: {
      compliant: 'Quality improvement program meets He-P 809.18 requirements.',
      atRisk: 'QI program needs additional components.',
      nonCompliant: 'No quality improvement program exists. This violates He-P 809.18.',
      unknown: 'Verify QI program.',
    },
    stateNotes: [
      'QI program required per He-P 809.18',
      'Regular clinical record reviews',
      'Track and analyze incidents',
      'Document QI activities',
    ],
  },

  {
    id: 'NH-F2',
    itemId: 'F2',
    stateCode: 'NH',
    title: 'Clinical Documentation',
    description: 'New Hampshire requires complete and timely clinical documentation',
    severity: 'high',
    regulationCode: 'He-P 809.14',
    regulationName: 'Documentation',
    agencyName: 'New Hampshire Department of Health and Human Services',
    agencyAbbr: 'DHHS',
    agencyPhone: '(603) 271-9039',
    agencyUrl: 'https://www.dhhs.nh.gov/programs-services/health-care/home-health-care',
    questions: [
      {
        id: 'q1',
        question: 'Which documentation practices does your agency follow?',
        inputType: 'checklist',
        options: [
          { value: 'visit_notes', label: 'Visit notes completed same day or within 24 hours' },
          { value: 'signatures', label: 'All entries signed and dated by clinician' },
          { value: 'legible', label: 'Documentation is legible and complete' },
          { value: 'corrections', label: 'Proper correction procedures (no white-out)' },
          { value: 'physician_orders', label: 'Physician orders obtained and documented' },
          { value: 'communication', label: 'Communication with physician documented' },
        ],
        required: true,
      },
    ],
    scoringRules: [
      { condition: 'countAtLeast', value: 6, result: 'COMPLIANT' },
      { condition: 'countAtLeast', value: 4, result: 'AT_RISK' },
      { condition: 'countLessThan', value: 4, result: 'NON_COMPLIANT' },
    ],
    reportLanguage: {
      compliant: 'Clinical documentation practices meet requirements.',
      atRisk: 'Documentation practices need improvement.',
      nonCompliant: 'Documentation practices are inadequate.',
      unknown: 'Review documentation requirements.',
    },
    stateNotes: [
      'Complete documentation for each visit',
      'Sign and date all entries',
      'Use proper correction procedures',
      'Document physician communication',
    ],
  },

  {
    id: 'NH-F3',
    itemId: 'F3',
    stateCode: 'NH',
    title: 'Complaint and Grievance Process',
    description: 'New Hampshire requires documented complaint handling procedures',
    severity: 'medium',
    regulationCode: 'He-P 809.11',
    regulationName: 'Complaints',
    agencyName: 'New Hampshire Department of Health and Human Services',
    agencyAbbr: 'DHHS',
    agencyPhone: '(603) 271-9039',
    agencyUrl: 'https://www.dhhs.nh.gov/programs-services/health-care/home-health-care',
    questions: [
      {
        id: 'q1',
        question: 'Do you have a documented process for handling patient complaints and grievances?',
        inputType: 'select',
        options: [
          { value: 'documented', label: 'Yes, documented process with tracking' },
          { value: 'informal', label: 'Informal process, not fully documented' },
          { value: 'no', label: 'No complaint handling process' },
        ],
        required: true,
        helpText: 'Per He-P 809.11: Department investigates complaints. Per He-P 809.14(b): Written complaint procedures required.',
      },
    ],
    scoringRules: [
      { condition: 'equals', value: 'documented', result: 'COMPLIANT' },
      { condition: 'equals', value: 'informal', result: 'AT_RISK' },
      { condition: 'equals', value: 'no', result: 'NON_COMPLIANT' },
    ],
    reportLanguage: {
      compliant: 'Complaint and grievance handling process is documented.',
      atRisk: 'Complaint handling process exists but may not be fully documented.',
      nonCompliant: 'No complaint handling process in place.',
      unknown: 'Verify complaint procedures.',
    },
    stateNotes: [
      'Written complaint procedures required',
      'Track and document all complaints',
      'DHHS may investigate complaints per He-P 809.11',
      'Inform patients of right to file complaints with DHHS',
    ],
  },

  {
    id: 'NH-F4',
    itemId: 'F4',
    stateCode: 'NH',
    title: 'Incident Reporting',
    description: 'New Hampshire requires documented incident reporting procedures',
    severity: 'high',
    regulationCode: 'He-P 809.14',
    regulationName: 'Incidents',
    agencyName: 'New Hampshire Department of Health and Human Services',
    agencyAbbr: 'DHHS',
    agencyPhone: '(603) 271-9039',
    agencyUrl: 'https://www.dhhs.nh.gov/programs-services/health-care/home-health-care',
    questions: [
      {
        id: 'q1',
        question: 'Do you have a documented incident reporting process?',
        inputType: 'select',
        options: [
          { value: 'documented', label: 'Yes, fully documented and followed consistently' },
          { value: 'informal', label: 'Yes, but informal / not fully documented' },
          { value: 'no', label: 'No incident reporting process in place' },
        ],
        required: true,
      },
    ],
    scoringRules: [
      { condition: 'equals', value: 'documented', result: 'COMPLIANT' },
      { condition: 'equals', value: 'informal', result: 'AT_RISK' },
      { condition: 'equals', value: 'no', result: 'NON_COMPLIANT' },
    ],
    reportLanguage: {
      compliant: 'Incident reporting process is documented and followed.',
      atRisk: 'Incident reporting process exists but may not be fully documented.',
      nonCompliant: 'No incident reporting process in place.',
      unknown: 'Verify incident reporting procedures.',
    },
    stateNotes: [
      'Document all incidents',
      'Analyze for trends',
      'Implement corrective actions',
      'Track outcomes',
    ],
  },

  // ============================================
  // SECTION G: INSPECTIONS & COMPLIANCE
  // ============================================
  {
    id: 'NH-G1',
    itemId: 'G1',
    stateCode: 'NH',
    title: 'DHHS Inspection Readiness',
    description: 'Preparation for DHHS compliance inspections per He-P 809.09',
    severity: 'high',
    regulationCode: 'He-P 809.09',
    regulationName: 'Inspection',
    agencyName: 'New Hampshire Department of Health and Human Services',
    agencyAbbr: 'DHHS',
    agencyPhone: '(603) 271-9039',
    agencyUrl: 'https://www.dhhs.nh.gov/programs-services/health-care/home-health-care',
    questions: [
      {
        id: 'q1',
        question: 'When was your last DHHS inspection?',
        inputType: 'select',
        options: [
          { value: 'recent', label: 'Within the last 12 months' },
          { value: 'over_12', label: 'More than 12 months ago' },
          { value: 'never', label: 'Never inspected / New agency' },
        ],
        required: true,
        helpText: 'Per He-P 809.09: DHHS may conduct inspections to determine compliance.',
      },
      {
        id: 'q2',
        question: 'Were any areas of non-compliance cited in your last inspection?',
        inputType: 'select',
        options: [
          { value: 'none', label: 'No areas of non-compliance' },
          { value: 'minor', label: 'Minor issues (POC submitted and accepted)' },
          { value: 'major', label: 'Major issues / enforcement actions' },
          { value: 'na', label: 'N/A - not yet inspected' },
        ],
        helpText: 'Per He-P 809.09(e): POC must be submitted within 21 days of inspection findings.',
      },
    ],
    scoringRules: [
      { condition: 'allTrue', value: [
        { condition: 'includes', questionId: 'q1', value: ['recent', 'over_12'] },
        { condition: 'includes', questionId: 'q2', value: ['none', 'minor'] },
      ], result: 'COMPLIANT' },
      { condition: 'equals', questionId: 'q2', value: 'major', result: 'AT_RISK' },
      { condition: 'equals', questionId: 'q1', value: 'never', result: 'AT_RISK' },
    ],
    reportLanguage: {
      compliant: 'Inspection history is satisfactory.',
      atRisk: 'Address any outstanding compliance issues. Submit POC within 21 days per He-P 809.09(e).',
      nonCompliant: 'Major compliance issues require immediate attention.',
      unknown: 'Contact DHHS to verify inspection status.',
    },
    stateNotes: [
      'DHHS may conduct inspections anytime per He-P 809.09',
      'Initial inspection required before license per He-P 809.05(f)',
      'POC due within 21 days of findings per He-P 809.09(e)',
      'Administrative remedies per He-P 809.12',
    ],
  },

  {
    id: 'NH-G2',
    itemId: 'G2',
    stateCode: 'NH',
    title: 'Medicare Certification Status',
    description: 'Medicare certification for home health agencies',
    severity: 'medium',
    regulationCode: '42 CFR 484',
    regulationName: 'Medicare CoPs',
    agencyName: 'Centers for Medicare & Medicaid Services',
    agencyAbbr: 'CMS',
    agencyPhone: '(800) 633-4227',
    agencyUrl: 'https://www.cms.gov/center/hha.asp',
    questions: [
      {
        id: 'q1',
        question: 'Does your agency participate in Medicare?',
        inputType: 'binary',
        options: [
          { value: 'yes', label: 'Yes' },
          { value: 'no', label: 'No' },
        ],
        required: true,
      },
      {
        id: 'q2',
        question: 'Is your Medicare certification current and in good standing?',
        inputType: 'select',
        options: [
          { value: 'current', label: 'Yes, current with no deficiencies' },
          { value: 'deficiencies', label: 'Current but with deficiencies' },
          { value: 'expired', label: 'Expired or suspended' },
        ],
        showCondition: {
          questionId: 'q1',
          operator: 'equals',
          value: 'yes',
        },
      },
    ],
    scoringRules: [
      { condition: 'equals', questionId: 'q1', value: 'no', result: 'COMPLIANT' },
      { condition: 'equals', questionId: 'q2', value: 'current', result: 'COMPLIANT' },
      { condition: 'equals', questionId: 'q2', value: 'deficiencies', result: 'AT_RISK' },
      { condition: 'equals', questionId: 'q2', value: 'expired', result: 'NON_COMPLIANT' },
    ],
    reportLanguage: {
      compliant: 'Medicare certification status is satisfactory.',
      atRisk: 'Address Medicare survey deficiencies promptly.',
      nonCompliant: 'Medicare certification has expired or been suspended.',
      unknown: 'Verify Medicare certification status.',
    },
    stateNotes: [
      'State license required before Medicare certification',
      'Must meet 42 CFR 484 Conditions of Participation',
      'DHHS conducts Medicare surveys',
    ],
  },
];

export default NEWHAMPSHIRE_QUESTIONS;